/**
 * Where a Paya ID sign-in lands when the person belongs to more than one
 * servable workshop (#2052).
 *
 * The apex parks a verified proof and the **allowed** tenants, then sends the
 * browser here with a random id in the fragment — never the tokens, never a
 * slug the browser invented. Choosing a workshop posts that id and a slug from
 * the server's list; the server refuses anything else.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { api } from '@/lib/api';
import { AuthCard } from './AuthCard';

interface WorkshopChoice {
  readonly slug: string;
  readonly name: string;
}

/** `#id=<token>` — the only thing the apex puts in front of the browser. */
function selectionIdFromFragment(hash: string): string | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const id = params.get('id');
  return id !== null && id !== '' ? id : null;
}

/**
 * The server owns this URL. Still: a `javascript:` value must never become a
 * navigation, even if a proxy mangled the response.
 */
function safeRedirectTo(value: unknown): string | null {
  if (typeof value !== 'string' || value === '') return null;
  if (value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\')) {
    return value;
  }
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return value;
  } catch {
    return null;
  }
}

export default function WorkshopSelectionPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const started = useRef(false);
  const [selectionId, setSelectionId] = useState<string | null>(null);
  const [workshops, setWorkshops] = useState<WorkshopChoice[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [choosing, setChoosing] = useState<string | null>(null);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const id = selectionIdFromFragment(window.location.hash);
    if (id === null) {
      setFailed(true);
      return;
    }
    window.history.replaceState(null, '', window.location.pathname);
    setSelectionId(id);

    void api
      .post<{ workshops: WorkshopChoice[] }>('/auth/oidc/workshops', { id })
      .then((body) => {
        if (!Array.isArray(body.workshops) || body.workshops.length < 2) {
          setFailed(true);
          return;
        }
        setWorkshops(body.workshops);
      })
      .catch(() => {
        setFailed(true);
      });
  }, []);

  const choose = (slug: string) => {
    if (selectionId === null || choosing !== null) return;
    setChoosing(slug);
    void api
      .post<{ redirectTo?: unknown }>('/auth/oidc/workshops/select', { id: selectionId, slug })
      .then((body) => {
        const redirectTo = safeRedirectTo(body.redirectTo);
        if (redirectTo === null) {
          setFailed(true);
          setChoosing(null);
          return;
        }
        window.location.assign(redirectTo);
      })
      .catch(() => {
        setFailed(true);
        setChoosing(null);
      });
  };

  return (
    <AuthCard>
      <Stack spacing={2}>
        {workshops === null && !failed && (
          <>
            <Stack alignItems="center" spacing={2}>
              <CircularProgress />
              <Typography variant="body1">{t('workshopSelection.loading')}</Typography>
            </Stack>
          </>
        )}

        {workshops !== null && !failed && (
          <>
            <Typography variant="h6" component="h1">
              {t('workshopSelection.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('workshopSelection.body')}
            </Typography>
            {workshops.map((workshop) => (
              <Button
                key={workshop.slug}
                variant="contained"
                fullWidth
                size="large"
                disabled={choosing !== null}
                onClick={() => choose(workshop.slug)}
              >
                {workshop.name}
              </Button>
            ))}
            <Button
              variant="text"
              fullWidth
              href="/api/v1/auth/oidc/start?intent=switch-account"
              disabled={choosing !== null}
            >
              {t('signInError.switchAccount')}
            </Button>
          </>
        )}

        {failed && (
          <>
            <Typography variant="h6" component="h1">
              {t('workshopSelection.failedTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('workshopSelection.failedBody')}
            </Typography>
            <Button
              variant="contained"
              fullWidth
              size="large"
              href="/api/v1/auth/oidc/start?intent=switch-account"
              sx={{ mt: 1 }}
            >
              {t('signInError.switchAccount')}
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => void navigate('/login', { replace: true })}
            >
              {t('workshopSelection.backToLogin')}
            </Button>
          </>
        )}
      </Stack>
    </AuthCard>
  );
}
